import { RotateCcw, MoreHorizontal, FileText, FileSpreadsheet, GitCompare, Upload, Send, Loader2, Building2, Package, Zap, Download, ArrowRightLeft, Search, X, Unlock } from 'lucide-react'
import { useState, useRef, useEffect, useCallback } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui'
import { useQuoteStore } from '../store'
import { useScopeHours } from '../../scope/useScopeHours'
import { useHomeStore } from '@/features/home/store'
import { quotesApi, dealsApi, draftsApi, getAuthHeaders } from '@api/client'
import { useSubmitForReview } from '../hooks/useSubmitForReview'
import { showToast } from '@store/uiStore'
import {
  exportQuoteToPDF,
  exportQuoteToCSV,
  exportUsageToPDF,
  exportUsageToCSV,
  exportComparisonPDF,
  exportResellerProposalPDF,
} from '../utils/exportUtils'
import { EXCHANGE_RATES } from '../data'

const API_BASE = '/api'

const SUBMIT_UNSYNCED_CONFIRM = [
  "You haven't synced the scope hours with this quote's implementation fee.",
  '',
  'Continuing will route this to the Implementations team (Alitia and Aju) to check',
  'whether the proposed fee lines up with the actual work and hours needed.',
  "You'll be notified in Slack once they're done.",
  '',
  'Continue with Implementations review?',
].join('\n')

interface QuoteHeaderProps {
  /** Re-fetch the deal after a successful submit so banners + locks reflect new state. */
  onReviewSubmitted?: () => void
  /** Current impl-review state, drives the submit button's label/title. */
  implReviewStatus?: string | null
  /** Current deal status. When 'finalized', the submit button re-opens + re-submits. */
  dealStatus?: string
}

export function QuoteHeader({ onReviewSubmitted, implReviewStatus, dealStatus }: QuoteHeaderProps = {}) {
  const { calculations, resetQuote, details, selectedProducts, partnership, quoteLocks } = useQuoteStore()
  const { submit: submitForReview, isSubmitting: isSubmittingReview } = useSubmitForReview()
  const { totalHours: scopeTotalHours } = useScopeHours()
  const { selectedDeal } = useHomeStore()
  const [searchParams] = useSearchParams()
  const [actionsOpen, setActionsOpen] = useState(false)
  const [isSendingToHubSpot, setIsSendingToHubSpot] = useState(false)
  const [isInsertingLineItems, setIsInsertingLineItems] = useState(false)
  const [isConverting, setIsConverting] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Deal picker state
  const [dealPickerOpen, setDealPickerOpen] = useState(false)
  const [dealSearch, setDealSearch] = useState('')
  const [dealResults, setDealResults] = useState<Array<{ id: number; customer_name: string; hubspot_deal_id: string; hubspot_deal_name: string }>>([])
  const [dealSearching, setDealSearching] = useState(false)
  const [reassigning, setReassigning] = useState(false)
  const dealPickerRef = useRef<HTMLDivElement>(null)

  const sessionDealId = searchParams.get('session')
  const draftId = searchParams.get('draft')
  const exportData = { details, selectedProducts, calculations, partnership }

  const handleReset = () => {
    if (confirm('Reset all fields to default values?')) {
      resetQuote()
    }
    setActionsOpen(false)
  }

  const handleSubmitForReview = async () => {
    if (!details.customerName) {
      showToast('Please enter a customer name before submitting for review.', 'warning')
      return
    }

    const dealId = sessionDealId || selectedDeal?.id
    if (!dealId) {
      showToast('Please link a deal before submitting for review. Go to Home and select a HubSpot deal first.', 'warning')
      return
    }

    // Implementation-fee ↔ scope sync status. The reviewer needs to know whether
    // the implementation fee was sourced from the scope; warn the submitter if a
    // scope exists but the fee isn't synced with it (never applied, or drifted).
    const scopeFee = scopeTotalHours * details.hourlyRate
    const feeFromScope = details.implementationFeeFromScope
    const feeStale = feeFromScope && Math.abs(details.implementationFee - scopeFee) > 0.5
    const feeSyncedWithScope = feeFromScope && !feeStale
    const needsSync = scopeTotalHours > 0 && !feeSyncedWithScope

    if (needsSync && !window.confirm(SUBMIT_UNSYNCED_CONFIRM)) {
      return
    }

    setActionsOpen(false)
    const result = await submitForReview(Number(dealId))
    if (result.ok) {
      // Re-fetch the deal so the banner + locks reflect the new state — S2 awaiting on a
      // server divert (PR 2), otherwise the standard in_review banner.
      onReviewSubmitted?.()
    }
  }

  // Re-open an approved (finalized) deal back to draft. The approved version is preserved as an
  // immutable snapshot in the Versions tab, so this is non-destructive. Returns true on success.
  // Without the re-open, any autosave/save against a finalized deal 423s
  // ("Quote cannot be edited after the deal is finalized").
  const reopenApprovedDeal = async (dealId: number): Promise<boolean> => {
    const reopen = await dealsApi.update(dealId, {
      status: 'draft',
      finalized_by: null,
      finalized_at: null,
      reviewed_by: null,
      reviewed_at: null,
      review_notes: null,
    } as Parameters<typeof dealsApi.update>[1])
    if (!reopen.success) {
      showToast(reopen.error || 'Failed to re-open the approved quote.', 'error')
      return false
    }
    return true
  }

  // Re-submit an already-approved quote, as-is: re-open to draft, then run the standard submit.
  const handleResubmit = async () => {
    const dealId = sessionDealId || selectedDeal?.id
    if (!dealId) {
      showToast('Please link a deal before submitting for review.', 'warning')
      return
    }
    setActionsOpen(false)
    if (!(await reopenApprovedDeal(Number(dealId)))) return
    const result = await submitForReview(Number(dealId))
    if (result.ok) onReviewSubmitted?.()
  }

  // Re-open an approved quote for editing: return it to draft and stay there (no auto-resubmit),
  // so the submitter can make changes and then submit for review through the normal flow.
  const handleReopenToEdit = async () => {
    const dealId = sessionDealId || selectedDeal?.id
    if (!dealId) {
      showToast('Please link a deal first.', 'warning')
      return
    }
    setActionsOpen(false)
    if (!(await reopenApprovedDeal(Number(dealId)))) return
    showToast('Quote re-opened for editing. The approved version is saved in the Versions tab.', 'success')
    // Re-fetch so the deal flips to draft → fields unlock for editing.
    onReviewSubmitted?.()
  }

  const handleSendToHubSpot = async () => {
    if (!selectedDeal?.id) {
      showToast('Please select a HubSpot deal from the Home page first.', 'warning')
      return
    }

    if (!details.customerName) {
      showToast('Please enter a customer name before sending to HubSpot.', 'warning')
      return
    }

    setIsSendingToHubSpot(true)
    setActionsOpen(false)

    try {
      const quoteLink = window.location.href
      const noteContent = `
📋 **DealHub Quote Created**

**Customer:** ${details.customerName}
**Prepared By:** ${details.preparedBy}
**Plan:** ${details.customerPlan}
**Contract Term:** ${details.contractTermMonths} months
**Currency:** ${details.displayCurrency}

**Financial Summary:**
• ARR: $${calculations?.arr?.toLocaleString() || 0}
• TCV: $${calculations?.tcv?.toLocaleString() || 0}
• Gross Profit Margin: ${calculations?.grossProfitMargin?.toFixed(1) || 0}%
• LTV/CAC Ratio: ${calculations?.ltvCacRatio?.toFixed(2) || 0}

**Deal Assessment:** ${calculations?.overallRating?.rating || 'N/A'}

**Products:** ${selectedProducts.length} products selected

🔗 View Quote: ${quoteLink}
      `.trim()

      const response = await fetch(`${API_BASE}/hubspot/deals/${selectedDeal.hubspotDealId || selectedDeal.id}/activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          type: 'note',
          content: noteContent,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to send to HubSpot')
      }

      showToast(`Quote logged to HubSpot deal: ${selectedDeal.name}`, 'success')
    } catch (error) {
      console.error('Send to HubSpot error:', error)
      showToast(error instanceof Error ? error.message : 'Failed to send to HubSpot.', 'error')
    } finally {
      setIsSendingToHubSpot(false)
    }
  }

  const handleInsertLineItems = async () => {
    const hubspotDealId = selectedDeal?.hubspotDealId || selectedDeal?.id
    if (!hubspotDealId) {
      showToast('Please select a HubSpot deal from the Home page first.', 'warning')
      return
    }

    if (selectedProducts.length === 0) {
      showToast('Please add at least one product before inserting line items.', 'warning')
      return
    }

    if (!details.customerPlan) {
      showToast('Please select a customer plan before inserting line items.', 'warning')
      return
    }

    const toAUD = (amount: number, fromCurrency: string): number => {
      if (fromCurrency === 'AUD') return amount
      return amount * (EXCHANGE_RATES[fromCurrency]?.AUD ?? 1)
    }

    const lineItems: { name: string; quantity: number; unitPrice: number; discountPercent: number }[] = []

    selectedProducts.forEach(p => {
      const discountPercent = p.discountType === 'percent' ? (p.discountValue || 0) : 0
      const priceInAUD = toAUD(p.effectivePrice, p.currency || 'USD')

      lineItems.push({
        name: p.name,
        quantity: p.volume || 1,
        unitPrice: Math.round(priceInAUD * 100) / 100,
        discountPercent,
      })
    })

    if (details.platformFees > 0) {
      lineItems.push({
        name: 'Platform Fees (Annual)',
        quantity: 1,
        unitPrice: details.platformFees,
        discountPercent: 0,
      })
    }

    if (details.implementationFee > 0) {
      lineItems.push({
        name: 'Implementation Fee',
        quantity: 1,
        unitPrice: details.implementationFee,
        discountPercent: 0,
      })
    }

    const confirmMsg = `This will replace all existing line items on the HubSpot deal "${selectedDeal?.name}" with ${lineItems.length} line items from this quote.\n\nProceed?`
    if (!confirm(confirmMsg)) return

    setIsInsertingLineItems(true)
    setActionsOpen(false)

    try {
      const response = await fetch(`${API_BASE}/hubspot/deals/${hubspotDealId}/line-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          lineItems,
          customerName: details.customerName,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to insert line items')
      }

      showToast(`Inserted ${result.lineItemCount} line items into HubSpot deal: ${selectedDeal?.name}`, 'success')
    } catch (error) {
      console.error('Insert line items error:', error)
      showToast(error instanceof Error ? error.message : 'Failed to insert line items.', 'error')
    } finally {
      setIsInsertingLineItems(false)
    }
  }

  const handleConvertDraft = async () => {
    if (!draftId || draftId === 'new') return
    setIsConverting(true)
    try {
      const res = await draftsApi.convert(Number(draftId))
      if (res.success && res.data) {
        navigate(`/quote?session=${res.data.dealId}`)
      } else {
        showToast(res.error || 'Failed to convert draft to session', 'error')
      }
    } catch (error) {
      console.error('Convert draft error:', error)
      showToast('Failed to convert draft. Please try again.', 'error')
    } finally {
      setIsConverting(false)
    }
  }

  // Deal search for reassign picker
  const doSearch = useCallback(async () => {
    if (dealSearch.length < 2) { setDealResults([]); return }
    setDealSearching(true)
    try {
      const res = await dealsApi.list({ search: dealSearch, pageSize: 10 })
      if (res.success && res.data) {
        // Exclude the current deal from results
        const currentId = sessionDealId ? Number(sessionDealId) : null
        const filtered = (res.data.items || []).filter((d: any) => d.id !== currentId)
        setDealResults(filtered as any)
      }
    } finally {
      setDealSearching(false)
    }
  }, [dealSearch, sessionDealId])

  useEffect(() => {
    if (!dealPickerOpen) return
    const timer = setTimeout(doSearch, 300)
    return () => clearTimeout(timer)
  }, [doSearch, dealPickerOpen])

  const handleReassign = async (targetDealId: number, targetDealName: string) => {
    if (!sessionDealId) return
    if (!confirm(`Move this quote to "${targetDealName}"? The quote will be detached from the current deal.`)) return

    setReassigning(true)
    try {
      const res = await quotesApi.reassign(Number(sessionDealId), targetDealId)
      if (res.success) {
        showToast(`Quote moved to ${targetDealName}`, 'success')
        setDealPickerOpen(false)
        setDealSearch('')
        setDealResults([])
        navigate(`/quote?session=${targetDealId}`)
      } else {
        showToast(res.error || 'Failed to reassign quote', 'error')
      }
    } catch (err) {
      console.error('Reassign error:', err)
      showToast('Failed to reassign quote', 'error')
    } finally {
      setReassigning(false)
    }
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActionsOpen(false)
      }
      if (dealPickerRef.current && !dealPickerRef.current.contains(event.target as Node)) {
        setDealPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Reusable dropdown button style
  const menuItemClass = "w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">
            Create your Quote
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Calculate deal profitability<br />and sync quotes with CRM
          </p>
        </div>

        {/* Center - Deal Tag with reassign */}
        <div className="flex-1 flex justify-center">
          <div className="relative" ref={dealPickerRef}>
            {selectedDeal ? (
              <button
                onClick={() => setDealPickerOpen(!dealPickerOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 border border-primary/30 rounded-full text-xs text-primary hover:bg-primary/20 transition-colors"
              >
                <Building2 size={12} />
                <span className="font-medium">{selectedDeal.name}</span>
                <span className="text-primary/60">#{selectedDeal.hubspotDealId || selectedDeal.id}</span>
                <ArrowRightLeft size={10} className="ml-1 opacity-60" />
              </button>
            ) : draftId ? (
              <button
                onClick={handleConvertDraft}
                disabled={isConverting || draftId === 'new'}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 rounded-full text-xs text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
              >
                {isConverting ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Zap size={12} />
                )}
                <span className="font-medium">{isConverting ? 'Creating session...' : 'Start a Session'}</span>
              </button>
            ) : (
              <Link
                to="/home"
                className="flex items-center gap-1.5 px-2.5 py-1 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-full text-xs text-[var(--text-muted)] hover:border-primary/30 hover:text-[var(--text-secondary)] transition-colors"
              >
                <Building2 size={12} />
                <span>Draft mode — link a deal to enable auto-save</span>
              </Link>
            )}

            {/* Deal picker dropdown */}
            {dealPickerOpen && selectedDeal && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-80 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-dropdown z-50">
                <div className="p-3 border-b border-[var(--border-color)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-[var(--text-secondary)]">Move quote to a different deal</span>
                    <button onClick={() => setDealPickerOpen(false)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                      <X size={14} />
                    </button>
                  </div>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    <input
                      type="text"
                      autoFocus
                      placeholder="Search deals..."
                      value={dealSearch}
                      onChange={(e) => setDealSearch(e.target.value)}
                      className="w-full h-9 pl-9 pr-3 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {dealSearching && (
                    <div className="flex items-center justify-center py-4 text-xs text-[var(--text-muted)]">
                      <Loader2 size={14} className="animate-spin mr-2" /> Searching...
                    </div>
                  )}
                  {!dealSearching && dealSearch.length >= 2 && dealResults.length === 0 && (
                    <div className="py-4 text-center text-xs text-[var(--text-muted)]">No deals found</div>
                  )}
                  {!dealSearching && dealSearch.length < 2 && (
                    <div className="py-4 text-center text-xs text-[var(--text-muted)]">Type at least 2 characters to search</div>
                  )}
                  {dealResults.map((deal: any) => (
                    <button
                      key={deal.id}
                      onClick={() => handleReassign(deal.id, deal.customer_name || deal.hubspot_deal_name || `Deal #${deal.id}`)}
                      disabled={reassigning}
                      className="w-full px-3 py-2 text-left hover:bg-[var(--bg-tertiary)] flex items-center justify-between gap-2 disabled:opacity-50"
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-[var(--text-primary)] truncate">
                          {deal.customer_name || deal.hubspot_deal_name || `Deal #${deal.id}`}
                        </div>
                        {deal.hubspot_deal_id && (
                          <div className="text-xs text-[var(--text-muted)]">#{deal.hubspot_deal_id}</div>
                        )}
                      </div>
                      <ArrowRightLeft size={12} className="shrink-0 text-[var(--text-muted)]" />
                    </button>
                  ))}
                </div>
                <div className="p-2 border-t border-[var(--border-color)]">
                  <Link
                    to="/home"
                    className="block text-center text-xs text-primary hover:underline"
                    onClick={() => setDealPickerOpen(false)}
                  >
                    Go to Home to view all deals
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Submit for Review — promoted to standalone button. Hidden entirely in the
              'reviewed' state (PR 5): the emerald banner's "Review change and continue" is
              the path forward there, and a greyed same-label button reads as broken.
              Also blocked when the quote is already locked (in_review) so the submitter
              can't accidentally re-submit and double-fire Slack. */}
          {/* Reopen-to-edit — only on approved (finalized) quotes. Returns the deal to draft so
              the submitter can edit, then submit through the normal flow. The approved version
              stays frozen in the Versions tab. Sits beside "Submit for review again" (as-is). */}
          {dealStatus === 'finalized' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleReopenToEdit}
              disabled={isSubmittingReview}
              title="Re-open this approved quote so you can edit it. The approved version stays in the Versions tab."
            >
              <Unlock size={14} className="mr-1.5" />
              Reopen to edit
            </Button>
          )}

          {implReviewStatus !== 'reviewed' && (
            <Button
              variant="primary"
              size="sm"
              onClick={dealStatus === 'finalized' ? handleResubmit : handleSubmitForReview}
              disabled={isSubmittingReview || (quoteLocks.all && dealStatus !== 'finalized')}
              title={
                implReviewStatus === 'awaiting'
                  ? "Implementations are reviewing — you'll be notified in Slack"
                  : dealStatus === 'finalized'
                    ? 'Re-open this approved quote and submit it for review again'
                    : quoteLocks.all
                      ? 'Already under review'
                      : undefined
              }
            >
              {isSubmittingReview ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Send size={14} className="mr-1.5" />
              )}
              {isSubmittingReview
                ? 'Submitting...'
                : implReviewStatus === 'awaiting'
                  ? 'Awaiting Impl Review'
                  : dealStatus === 'finalized'
                    ? 'Submit for review again'
                    : quoteLocks.all
                      ? 'Under Review'
                      : 'Submit for Review'}
            </Button>
          )}

          {/* Actions Dropdown — secondary actions only */}
          <div className="relative" ref={dropdownRef}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActionsOpen(!actionsOpen)}
            >
              <MoreHorizontal size={14} className="mr-1.5" />
              More
            </Button>

            {actionsOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-dropdown z-50">
                <div className="py-1">
                  {/* Export Section */}
                  <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] uppercase tracking-wider font-medium">
                    Export
                  </div>
                  <button className={menuItemClass} onClick={() => { exportQuoteToPDF(exportData); setActionsOpen(false) }}>
                    <FileText size={14} className="text-primary" />
                    Quote PDF
                  </button>
                  <button className={menuItemClass} onClick={() => { exportQuoteToCSV(exportData); setActionsOpen(false) }}>
                    <FileSpreadsheet size={14} className="text-success" />
                    Quote CSV
                  </button>
                  <button className={menuItemClass} onClick={() => { exportComparisonPDF(exportData); setActionsOpen(false) }}>
                    <GitCompare size={14} className="text-primary" />
                    Comparison PDF
                  </button>
                  {partnership.isReseller && (
                    <button className={menuItemClass} onClick={() => { exportResellerProposalPDF(exportData); setActionsOpen(false) }}>
                      <Building2 size={14} className="text-amber-500" />
                      Reseller Proposal PDF
                    </button>
                  )}
                  <button className={menuItemClass} onClick={() => { exportUsageToPDF(exportData); setActionsOpen(false) }}>
                    <Download size={14} className="text-[var(--text-muted)]" />
                    Usage Report PDF
                  </button>
                  <button className={menuItemClass} onClick={() => { exportUsageToCSV(exportData); setActionsOpen(false) }}>
                    <Download size={14} className="text-[var(--text-muted)]" />
                    Usage Report CSV
                  </button>

                  <div className="border-t border-[var(--border-color)] my-1" />

                  {/* HubSpot Section */}
                  <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] uppercase tracking-wider font-medium">
                    HubSpot
                  </div>
                  <button
                    className={menuItemClass}
                    onClick={handleInsertLineItems}
                    disabled={isInsertingLineItems}
                  >
                    {isInsertingLineItems ? (
                      <Loader2 size={14} className="text-[#ff7a59] animate-spin" />
                    ) : (
                      <Package size={14} className="text-[#ff7a59]" />
                    )}
                    {isInsertingLineItems ? 'Inserting...' : 'Insert Line Items'}
                  </button>
                  <button
                    className={menuItemClass}
                    onClick={handleSendToHubSpot}
                    disabled={isSendingToHubSpot}
                  >
                    {isSendingToHubSpot ? (
                      <Loader2 size={14} className="text-[#ff7a59] animate-spin" />
                    ) : (
                      <Upload size={14} className="text-[#ff7a59]" />
                    )}
                    {isSendingToHubSpot ? 'Sending...' : 'Log Note to HubSpot'}
                  </button>

                  <div className="border-t border-[var(--border-color)] my-1" />

                  {/* Reset */}
                  <button className={menuItemClass} onClick={handleReset}>
                    <RotateCcw size={14} className="text-[var(--text-muted)]" />
                    Reset Calculator
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
